// Text for the SaH Dashboard (TAV-3342 to TAV-3348, rebuilt in TAV-3463, changed in TAV-3482): header, case manager
// filter, banner, Company Summary, both overviews, the three Over/Under lists, the worker list and the full tables. The
// single source for every title, description, label, tooltip and note on the page; the Confluence pages mirror this
// text, so change both together. Term definitions shared with the Budget Tracker come from BUDGET_TERMS.
import { BUDGET_TERMS } from '~support-at-home/lib/budgetTerms';

import { pluralise } from '~support-at-home/components/budgets/tracker/transforms';

// The Budget column, shared by every list and full table.
const budgetColumn = {
  header: 'Budget',
  caption: "The budget's reference. Opens its Budget Tracker.",
  linkLabel: (reference: string, client: string) => `Open the Budget Tracker for ${reference}, ${client}`,
};

const clientColumn = {
  header: 'Client',
  caption: 'A client with two budgets in the quarter is listed twice, once for each.',
};

const fundingTypeColumn = {
  header: 'Funding Type',
  caption: "The funding type of the budget's client package, as on the budget list and its Budget Tracker.",
};

export const DASHBOARD_TEXT = {
  header: {
    title: 'Company Budget Dashboard',
    included: 'Included',
    budgetsLabel: 'budgets',
    clientsLabel: 'clients',
    counts: (budgets: number, clients: number) => `: ${pluralise(budgets, 'budget')} · ${pluralise(clients, 'client')}`,
    includedHint:
      'Active budgets whose dates overlap the selected quarter, for the selected case manager. Each budget is ' +
      'counted in full, as on its Budget Tracker.',
  },
  filter: { label: 'Case manager', all: 'All case managers', unassigned: 'Unassigned' },
  refresh: {
    lastCalculated: 'Last Calculated At',
    lastCalculatedHint:
      'When the oldest figures on this page were worked out. Figures are reused for a while so the page loads ' +
      'faster; press Recalculate Dashboard (the refresh icon) to work them all out again now.',
    time: (time: string) => ` : ${time}`,
    button: 'Recalculate Dashboard',
    failed: (message: string) => `The figures did not refresh, so the page still shows the earlier ones: ${message}`,
  },
  banner: {
    title: (count: number) => `${count} ${count === 1 ? 'item is' : 'items are'} over a limit this quarter`,
    overAllocated: (amount: string, count: number) =>
      `Over-allocated (over SAH funding): ${amount} ${count === 1 ? 'on' : 'across'} ${pluralise(count, 'budget')}. ${
        BUDGET_TERMS.prompts.updateBudget
      }`,
    overUtilised: (amount: string, count: number) =>
      `Over-utilised (over set budget): ${amount} ${count === 1 ? 'on' : 'across'} ${pluralise(count, 'budget')}. ${
        BUDGET_TERMS.prompts.updateBudget
      }`,
    careManagement: (amount: string, count: number) =>
      `Care management over allowance: ${amount} ${count === 1 ? 'on' : 'across'} ${pluralise(count, 'budget')}`,
  },
  summary: {
    title: 'Company Summary',
    description: {
      before: 'Company-wide figures for the selected quarter, from every ',
      term: 'Active status',
      termHint: 'Draft, Inactive, Needs Review and Deleted status budgets are excluded.',
      after: ' budget whose dates overlap it, each counted in full as on its Budget Tracker.',
    },
    section:
      'Company-wide figures in two steps. Allocation compares the agreed budgets with the clients’ funding; ' +
      'Utilisation shows how much of the agreed budgets is booked and claimed. Rates come from the totals, not an ' +
      'average of each budget. Each underlined figure name explains itself on hover or keyboard focus.',
    allocationTitle: 'Allocation',
    allocationSubtitle: 'Agreed budgets vs funding',
    utilisationTitle: 'Utilisation',
    utilisationSubtitle: 'Booked and claimed vs agreed budgets',
    available: {
      label: 'Available',
      line: 'Funding for period',
      tooltip: `Each included budget's Available added up, including ${BUDGET_TERMS.available.includes}, as on its Budget Tracker.`,
    },
    allocated: {
      label: 'Allocated',
      line: 'Agreed budget',
      tooltip: "Each included budget's Allocated added up. A budget that runs past the quarter counts in full.",
    },
    residual: {
      label: 'Residual',
      line: BUDGET_TERMS.unallocated.formula,
      tooltip:
        `${BUDGET_TERMS.unallocated.formula}, added up across the included budgets. A budget over its funding lowers ` +
        "this total, so it also nets against other budgets' unused funding. The Allocation lists below show each " +
        'budget on its own.',
    },
    allocationRate: {
      label: 'Allocation Rate',
      line: BUDGET_TERMS.allocationRate.formula,
      tooltip: `${BUDGET_TERMS.allocationRate.formula}, from the totals across every included budget, not an average of each budget's rate.`,
    },
    booked: {
      label: 'Booked',
      line: 'Not yet claimed',
      tooltip:
        "Each included budget's Booked added up, over each budget's own dates, not only the quarter: appointments and " +
        'approved supplier invoices not yet in a finalised claim. A budget over its agreed budget adds all its ' +
        'booked work here, so this can be more than the Company Budget Overview shows against Allocated.',
    },
    claimed: {
      label: 'Claimed',
      line: 'Submitted or finalised claims',
      tooltip:
        "Each included budget's Claimed added up, over each budget's own dates, not only the quarter: appointments " +
        'and supplier invoices in a submitted or finalised claim. A budget over its agreed budget adds all its ' +
        'claimed work here.',
    },
    remaining: {
      label: 'Remaining',
      line: 'Left to book',
      tooltip:
        `Each included budget's Remaining added up (${BUDGET_TERMS.remaining.formula}). A budget below zero lowers ` +
        "this total, so it nets against other budgets' unused budget. The Utilisation lists below show each budget " +
        'on its own.',
    },
    utilisationRate: {
      label: 'Utilisation Rate',
      line: BUDGET_TERMS.utilisationRate.formula,
      tooltip: `${BUDGET_TERMS.utilisationRate.formula}, from the totals across every included budget, not an average of each budget's rate.`,
    },
  },
  overview: {
    title: 'Company Budget Overview',
    description: 'Booked and claimed against the agreed budgets, added up across the included budgets.',
    section:
      'How the included budgets’ agreed budgets (Allocated) are used, all added up. The ring fills up to the total ' +
      'Allocated; if Booked and Claimed together come to more than it, the amount beyond runs on as a second, amber ' +
      'ring outside the first.',
    centreLabel: 'Allocated',
    booked: {
      label: 'Booked',
      tooltip:
        'Booked added up across the included budgets: appointments and approved supplier invoices not yet in a ' +
        `finalised claim. ${BUDGET_TERMS.booked.occurrences} ${BUDGET_TERMS.booked.cancellations} ` +
        'Shown here up to the total Allocated, claimed counting first; any more is under Over the agreed budgets.',
    },
    claimed: {
      label: 'Claimed',
      tooltip:
        'Claimed added up across the included budgets: appointments and supplier invoices in a submitted or ' +
        `finalised claim, ${BUDGET_TERMS.claimed.value}. ${BUDGET_TERMS.claimed.ignored} Shown here up to the ` +
        'total Allocated, claimed counting first; any more is under Over the agreed budgets.',
    },
    notYetBooked: {
      label: 'Not yet booked',
      tooltip: 'The total Allocated less Booked and Claimed. Zero once the agreed budgets are used up.',
    },
    // The legend row under the ring, only when over, with its booked and claimed parts (TAV-3482)
    over: {
      label: 'Over the agreed budgets',
      tooltip:
        'Booked and Claimed together beyond the total Allocated, claimed counting against Allocated first. It is ' +
        'the amber second ring outside the first.',
      booked: 'Booked',
      claimed: 'Claimed',
    },
    empty: 'No budget figures for the selected period',
  },
  careManagement: {
    title: 'Care Management Overview',
    description: 'Care management used against the allowances, added up across the included budgets.',
    section:
      'Care management used against the allowances of the included budgets that can have care management, all added ' +
      'up. The ring fills up to the total allowance; if use comes to more than it, the amount beyond runs on as a ' +
      'second, amber ring outside the first.',
    centreLabel: 'Allowance',
    used: {
      label: 'Used',
      tooltip: (booked: string, claimed: string, allowance: string) =>
        `Care management booked and claimed: ${booked} booked and ${claimed} claimed. ` +
        `${BUDGET_TERMS.careManagement.used} The allowances add up to ${allowance}.`,
    },
    unused: {
      label: 'Unused',
      tooltip: 'The total allowance less care management used. Zero once the allowances are used up.',
    },
    over: { label: 'Over the allowances' },
    empty: 'No care management allowance or use in the selected quarter.',
  },
  allocation: {
    title: 'Allocation',
    description: 'Agreed budgets against the clients’ funding, biggest amount first.',
    info:
      "Each budget's Allocated compared with its Available. Over-allocated lists the budgets that commit more than " +
      'the client has, by how much. Under-allocated lists the budgets that leave funding unused, by the Residual. ' +
      'A budget exactly at its funding is in neither list.',
    over: {
      label: 'Over-allocated',
      amountHeader: 'Over by',
      amountCaption: 'Allocated minus Available: how much more the agreed budget commits than the client has.',
      fullTitle: 'All over-allocated budgets',
      empty: 'No budget is over-allocated this quarter.',
    },
    under: {
      label: 'Under-allocated',
      amountHeader: 'Residual',
      amountCaption: `${BUDGET_TERMS.unallocated.formula}: funding the agreed budget leaves unused.`,
      fullTitle: 'All under-allocated budgets',
      empty: 'No budget is under-allocated this quarter.',
    },
  },
  careManagementList: {
    title: 'Care Management',
    description: 'Care management against each budget’s allowance, biggest amount first.',
    info:
      "Each budget's care management used (booked and claimed) compared with its allowance. Over allowance lists " +
      'the budgets that used more, by how much. Under allowance lists the budgets with allowance left, including ' +
      'budgets with none used. A budget exactly at its allowance is in neither list.',
    over: {
      label: 'Over allowance',
      amountHeader: 'Over by',
      amountCaption: 'Care management used minus the allowance, claimed counting against the allowance first.',
      fullTitle: 'All budgets over their care management allowance',
      empty: 'No budget has used more care management than its allowance this quarter.',
    },
    under: {
      label: 'Under allowance',
      amountHeader: 'Unused',
      amountCaption: 'The allowance minus care management used.',
      fullTitle: 'All budgets under their care management allowance',
      empty: 'No budget has care management allowance left this quarter.',
    },
  },
  utilisation: {
    title: 'Utilisation',
    description: 'Booked and claimed against each budget’s agreed budget, biggest amount first.',
    info:
      "Each budget's Booked plus Claimed compared with its Allocated. Over-utilised lists the budgets with more " +
      'booked and claimed than they allocated, by how much, with claimed counting first. Under-utilised lists the ' +
      'budgets with budget left to book. A budget exactly used up is in neither list.',
    over: {
      label: 'Over-utilised',
      amountHeader: 'Over by',
      amountCaption: 'Booked plus Claimed, minus Allocated, claimed counting against Allocated first.',
      fullTitle: 'All over-utilised budgets',
      empty: 'No budget is over-utilised this quarter.',
    },
    under: {
      label: 'Under-utilised',
      amountHeader: 'Left to book',
      amountCaption: 'Allocated minus Booked minus Claimed, for budgets that are within their Allocated.',
      fullTitle: 'All under-utilised budgets',
      empty: 'No budget has budget left to book this quarter.',
    },
  },
  workers: {
    title: 'Care Management by Worker',
    description: 'Care management hours and the average per working day, for each worker this quarter.',
    info:
      `${BUDGET_TERMS.cmByWorker.calculation} Workers with no care management hours are listed when Show Workers ` +
      'with No CM Hours is ticked.',
    fullTitle: 'All care management workers',
    empty: 'No care management appointments dated in the selected quarter on the included budgets.',
    tabs: { most: 'Most Hours First', least: 'Least Hours First' },
    noCmHours: 'Show Workers with No CM Hours',
    worker: {
      header: 'Worker',
      caption: 'The care worker the appointments are rostered to. Workers with the same hours are listed by name.',
    },
    cmHours: {
      header: 'CM Hours',
      caption: "The worker's care management appointments in the quarter, cancelled ones left out, in hours.",
    },
    hoursPerWorkingDay: {
      header: 'Average CM Working Hours Per Working Day (EST.)',
      caption: BUDGET_TERMS.cmByWorker.formula,
    },
    search: 'Search by worker',
  },
  list: {
    showing: (shown: number, total: number) => `Showing ${shown} of ${total}`,
    showingAll: (total: number) => `Showing all ${total}`,
    showAll: 'View full table',
    showAllLabel: (title: string) => `Expand ${title}`,
    search: 'Search by client or budget',
    noMatch: 'Nothing matches that search.',
    close: 'Close',
    exportTooltip: 'Export All to XLSX',
    exportLabel: (title: string) => `Export all of ${title} to XLSX`,
    exported: 'Exported',
    exportFailed: 'The export did not work. Please try again.',
  },
  columns: {
    client: clientColumn,
    budget: budgetColumn,
    fundingType: fundingTypeColumn,
    caseManager: {
      header: 'Case manager',
      caption: "The client's case manager, so a list can be sent to the right person.",
      unassigned: 'Unassigned',
    },
    cmAppointments: {
      header: 'CM appointments',
      caption:
        "The budget's care management appointments, booked, claimed or still to come, on every care management " +
        'service type, cancelled ones left out. Zero when the client has none.',
    },
    available: { header: 'Available', caption: "The client's funding for the budget's dates." },
    allocated: { header: 'Allocated', caption: 'The agreed budget: every line in it added up.' },
    allowance: { header: 'Allowance', caption: BUDGET_TERMS.careManagement.allowance },
    booked: { header: 'Booked', caption: 'Not yet in a finalised claim.' },
    claimed: { header: 'Claimed', caption: 'In a submitted or finalised claim.' },
    bookedOver: {
      header: 'Booked over',
      caption: 'The part of the amount over the limit that is booked, with claimed counting first.',
    },
    claimedOver: {
      header: 'Claimed over',
      caption: 'The part of the amount over the limit that is claimed, with claimed counting first.',
    },
    utilisation: {
      header: 'Utilisation',
      caption: `${BUDGET_TERMS.utilisationRate.formula}. n/a when nothing is allocated.`,
    },
    usage: {
      header: 'Usage',
      caption: 'Care management used divided by the allowance. n/a when there is no allowance.',
    },
    allocationRate: {
      header: 'Allocation',
      caption: `${BUDGET_TERMS.allocationRate.formula}. n/a when nothing is available.`,
    },
  },
  widget: {
    failed: (message: string) => `These figures did not load: ${message}`,
    retry: 'Retry',
    retryLabel: (title: string) => `Retry loading ${title}`,
  },
  noBudgets: 'No active budgets overlap this quarter for this case manager.',
} as const;
